import { useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Star, Search, Filter, Eye, Check, X, Trash2, Award, User, Building, Calendar, Plus, Link } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { reviewsService, type Review, type ReviewFilters, type CreateReviewRequest } from "@/services/reviewsService";

export default function ReviewsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ReviewFilters>({
    page: 1,
    limit: 10
  });
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<CreateReviewRequest>({
    bookingId: '',
    rating: 5,
    comment: '',
    media: []
  });
  const [mediaUrls, setMediaUrls] = useState<string>('');

  // Fetch reviews data
  const { data: reviewsResponse, isLoading: reviewsLoading, error: reviewsError } = useQuery({
    queryKey: ['reviews', filters],
    queryFn: () => reviewsService.getReviews(filters),
    staleTime: 30000,
  });

  const allReviews = reviewsResponse?.data?.reviews || [];
  
  // Apply client-side filtering as fallback if API doesn't handle filters
  const reviews = React.useMemo(() => {
    let filtered = [...allReviews];
    
    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter((review: any) => 
        review.comment?.toLowerCase().includes(searchTerm) ||
        review.user?.name?.toLowerCase().includes(searchTerm) ||
        review.property?.name?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply rating filter
    if (filters.rating) {
      filtered = filtered.filter((review: any) => review.rating === filters.rating);
    }
    
    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((review: any) => review.status === filters.status);
    }
    
    // Apply featured filter
    if (filters.is_featured !== undefined) {
      filtered = filtered.filter((review: any) => review.is_featured === filters.is_featured);
    }
    
    console.log('🔧 Reviews filtering applied:', {
      original: allReviews.length,
      filtered: filtered.length,
      filters
    });
    
    return filtered;
  }, [allReviews, filters]);
  
  // Add logging when data changes
  React.useEffect(() => {
    console.log('🔧 Reviews query result:', reviewsResponse);
    console.log('🔧 Reviews error:', reviewsError);
    console.log('🔧 All reviews:', allReviews);
    console.log('🔧 Filtered reviews:', reviews);
    console.log('🔧 Current filters:', filters);
  }, [reviewsResponse, reviewsError, allReviews, reviews, filters]);
  
  // Calculate stats from reviews data
  const calculatedStats = {
    total_reviews: reviews.length,
    pending_reviews: reviews.filter((r: any) => r.status === 'pending').length,
    approved_reviews: reviews.filter((r: any) => r.status === 'approved').length,
    rejected_reviews: reviews.filter((r: any) => r.status === 'rejected').length,
    featured_reviews: reviews.filter((r: any) => r.is_featured).length,
    average_rating: reviews.length > 0 ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length : 0
  };
  
  const stats = calculatedStats;

  // Create review mutation
  const createReviewMutation = useMutation({
    mutationFn: (data: CreateReviewRequest) => reviewsService.createReview(data),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("reviewCreatedSuccessfully") || "Review created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to create review",
        variant: "destructive",
      });
    },
  });

  // Update review mutation
  const updateReviewMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => reviewsService.updateReview(id, data),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("reviewUpdatedSuccessfully") || "Review updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to update review",
        variant: "destructive",
      });
    },
  });

  // Delete review mutation
  const deleteReviewMutation = useMutation({
    mutationFn: (id: number) => reviewsService.deleteReview(id),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("reviewDeletedSuccessfully") || "Review deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to delete review",
        variant: "destructive",
      });
    },
  });

  // Toggle featured mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: ({ id, is_featured }: { id: number; is_featured: boolean }) => 
      reviewsService.toggleFeatured(id, is_featured),
    onSuccess: () => {
      toast({
        title: t("success"),
        description: t("reviewFeaturedToggled") || "Review featured status updated",
      });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to update featured status",
        variant: "destructive",
      });
    },
  });

  const handleFilterChange = (key: keyof ReviewFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const resetForm = () => {
    setFormData({
      bookingId: '',
      rating: 5,
      comment: '',
      media: []
    });
    setMediaUrls('');
  };

  const handleCreateReview = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.bookingId.trim()) {
      toast({
        title: t("error"),
        description: t("bookingIdRequired") || "Booking ID is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.comment.trim()) {
      toast({
        title: t("error"),
        description: t("commentRequired") || "Comment is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.rating < 1 || formData.rating > 5) {
      toast({
        title: t("error"),
        description: t("ratingInvalid") || "Rating must be between 1 and 5",
        variant: "destructive",
      });
      return;
    }

    // Process media URLs
    const mediaArray = mediaUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);

    const reviewData: CreateReviewRequest = {
      ...formData,
      media: mediaArray.length > 0 ? mediaArray : undefined
    };

    createReviewMutation.mutate(reviewData);
  };

  const handleApproveReview = (id: number) => {
    updateReviewMutation.mutate({ id, data: { status: 'approved' } });
  };

  const handleRejectReview = (id: number) => {
    updateReviewMutation.mutate({ id, data: { status: 'rejected' } });
  };

  const handleToggleFeatured = (id: number, currentFeatured: boolean) => {
    toggleFeaturedMutation.mutate({ id, is_featured: !currentFeatured });
  };

  const handleDeleteReview = (id: number) => {
    deleteReviewMutation.mutate(id);
  };

  const handleViewReview = (review: Review) => {
    setSelectedReview(review);
    setIsViewModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: 'secondary',
      approved: 'default',
      rejected: 'destructive'
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {t(status) || status}
      </Badge>
    );
  };

  if (reviewsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">{t("loading")}...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("reviews") || "Reviews"}</h1>
          <p className="text-muted-foreground">
            {t("reviewsSubtitle") || "Manage customer reviews and ratings"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreateReview}>
            <Plus className="mr-2 h-4 w-4" />
            {t("addReview") || "Add Review"}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              console.log('🔧 Testing direct API call...');
              try {
                const response = await fetch('http://localhost:3000/admin/reviews', {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                });
                const data = await response.json();
                console.log('🔧 Direct API response:', data);
                console.log('🔧 Response status:', response.status);
              } catch (error) {
                console.error('🔧 Direct API error:', error);
              }
            }}
          >
            Test API
            </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalReviews") || "Total Reviews"}</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_reviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pendingReviews") || "Pending"}</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending_reviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("approvedReviews") || "Approved"}</CardTitle>
            <Check className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approved_reviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("rejectedReviews") || "Rejected"}</CardTitle>
            <X className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.rejected_reviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("featuredReviews") || "Featured"}</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.featured_reviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("averageRating") || "Avg Rating"}</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.average_rating.toFixed(1)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("filters") || "Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder={t("searchReviews") || "Search by comment, user, or property..."}
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select
              value={filters.rating?.toString() || 'all'}
              onValueChange={(value) => handleFilterChange('rating', value === 'all' ? undefined : Number(value))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("rating") || "Rating"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allRatings") || "All Ratings"}</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("status") || "Status"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                <SelectItem value="pending">{t("pending") || "Pending"}</SelectItem>
                <SelectItem value="approved">{t("approved") || "Approved"}</SelectItem>
                <SelectItem value="rejected">{t("rejected") || "Rejected"}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.is_featured !== undefined ? filters.is_featured.toString() : 'all'}
              onValueChange={(value) => handleFilterChange('is_featured', value === 'all' ? undefined : value === 'true')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("featured") || "Featured"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allReviews") || "All Reviews"}</SelectItem>
                <SelectItem value="true">{t("featured") || "Featured"}</SelectItem>
                <SelectItem value="false">{t("notFeatured") || "Not Featured"}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  search: '',
                  rating: undefined,
                  status: undefined,
                  is_featured: undefined,
                  page: 1,
                  limit: 10
                });
              }}
              className="whitespace-nowrap"
            >
              {t("clearFilters") || "Clear Filters"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("reviewsList") || "Reviews List"}</CardTitle>
              <CardDescription>
                {t("manageReviews") || "Manage customer reviews and ratings"}
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              {reviews.length > 0 ? (
                <>
                  {t("showing") || "Showing"} {reviews.length} {t("of") || "of"} {allReviews.length} {t("reviews") || "reviews"}
                  {(filters.search || filters.rating || filters.status || filters.is_featured !== undefined) && (
                    <span className="ml-2 text-primary">({t("filtered") || "filtered"})</span>
                  )}
                </>
              ) : (
                <span>{t("noReviews") || "No reviews found"}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <Star className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                {t("noReviews") || "No reviews found"}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("noReviewsMatch") || "No reviews match your current filters"}
              </p>
                      </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("user") || "User"}</TableHead>
                  <TableHead>{t("property") || "Property"}</TableHead>
                  <TableHead>{t("rating") || "Rating"}</TableHead>
                  <TableHead>{t("comment") || "Comment"}</TableHead>
                  <TableHead>{t("status") || "Status"}</TableHead>
                  <TableHead>{t("featured") || "Featured"}</TableHead>
                  <TableHead>{t("date") || "Date"}</TableHead>
                  <TableHead>{t("actions") || "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review: any) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={review.user?.avatar} />
                          <AvatarFallback>
                            {review.user?.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{review.user?.name || 'Unknown User'}</div>
                          <div className="text-sm text-muted-foreground">{review.user?.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{review.property?.name || 'Unknown Property'}</div>
                          <div className="text-sm text-muted-foreground">{review.property?.type}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {renderStars(review.rating)}
                        <span className="ml-1 text-sm font-medium">{review.rating}</span>
                        </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate">
                        {review.comment || 'No comment'}
                        </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(review.status)}
                    </TableCell>
                    <TableCell>
                      {review.is_featured ? (
                        <Badge variant="default" className="gap-1">
                          <Award className="h-3 w-3" />
                          {t("featured") || "Featured"}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(review.created_at)}
                        </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewReview(review)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {review.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApproveReview(review.id)}
                              disabled={updateReviewMutation.isPending}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRejectReview(review.id)}
                              disabled={updateReviewMutation.isPending}
                            >
                              <X className="h-4 w-4 text-red-600" />
                          </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeatured(review.id, review.is_featured)}
                          disabled={toggleFeaturedMutation.isPending}
                        >
                          <Award className={`h-4 w-4 ${review.is_featured ? 'text-yellow-500' : 'text-gray-400'}`} />
                          </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={deleteReviewMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                          </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("deleteReview") || "Delete Review"}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("deleteReviewConfirmation") || "Are you sure you want to delete this review? This action cannot be undone."}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteReview(review.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("delete") || "Delete"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Review Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("addReview") || "Add Review"}</DialogTitle>
            <DialogDescription>
              {t("addReviewDescription") || "Create a new review for a booking"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bookingId">{t("bookingId") || "Booking ID"}</Label>
                <Input
                  id="bookingId"
                  value={formData.bookingId}
                  onChange={(e) => setFormData(prev => ({ ...prev, bookingId: e.target.value }))}
                  placeholder="1"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating">{t("rating") || "Rating"}</Label>
                <Select
                  value={formData.rating.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, rating: Number(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 ⭐</SelectItem>
                    <SelectItem value="2">2 ⭐⭐</SelectItem>
                    <SelectItem value="3">3 ⭐⭐⭐</SelectItem>
                    <SelectItem value="4">4 ⭐⭐⭐⭐</SelectItem>
                    <SelectItem value="5">5 ⭐⭐⭐⭐⭐</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment">{t("comment") || "Comment"}</Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={t("enterComment") || "Enter your review comment..."}
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="media" className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                {t("mediaUrls") || "Media URLs (Optional)"}
              </Label>
              <Textarea
                id="media"
                value={mediaUrls}
                onChange={(e) => setMediaUrls(e.target.value)}
                placeholder={`${t("enterMediaUrls") || "Enter media URLs, one per line:"}\nhttps://cdn.site.com/review1.jpg\nhttps://cdn.site.com/review2.jpg`}
                rows={3}
              />
              <p className="text-sm text-muted-foreground">
                {t("mediaUrlsHelp") || "Enter one URL per line. Leave empty if no media."}
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateModalOpen(false)}
              >
                {t("cancel") || "Cancel"}
              </Button>
              <Button
                type="submit"
                disabled={createReviewMutation.isPending}
              >
                {createReviewMutation.isPending ? (t("creating") || "Creating...") : (t("createReview") || "Create Review")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Review Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("reviewDetails") || "Review Details"}</DialogTitle>
            <DialogDescription>
              {t("viewReviewDetails") || "View detailed information about this review"}
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">{t("customer") || "Customer"}</h4>
                  <div className="flex items-center gap-2">
                    <Avatar>
                      <AvatarImage src={selectedReview.user?.avatar} />
                      <AvatarFallback>
                        {selectedReview.user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{selectedReview.user?.name || 'Unknown User'}</div>
                      <div className="text-sm text-muted-foreground">{selectedReview.user?.email}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("property") || "Property"}</h4>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{selectedReview.property?.name || 'Unknown Property'}</div>
                      <div className="text-sm text-muted-foreground">{selectedReview.property?.type}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t("rating") || "Rating"}</h4>
                <div className="flex items-center gap-2">
                  {renderStars(selectedReview.rating)}
                  <span className="font-medium">{selectedReview.rating} / 5</span>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">{t("comment") || "Comment"}</h4>
                <p className="text-sm bg-muted p-3 rounded-md">
                  {selectedReview.comment || 'No comment provided'}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">{t("status") || "Status"}</h4>
                  {getStatusBadge(selectedReview.status)}
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("featured") || "Featured"}</h4>
                  {selectedReview.is_featured ? (
                    <Badge variant="default" className="gap-1">
                      <Award className="h-3 w-3" />
                      {t("yes") || "Yes"}
                  </Badge>
                  ) : (
                    <Badge variant="secondary">{t("no") || "No"}</Badge>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t("date") || "Date"}</h4>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(selectedReview.created_at)}
                  </div>
                </div>
              </div>
              
              {selectedReview.booking && (
                <div>
                  <h4 className="font-semibold mb-2">{t("booking") || "Booking"}</h4>
                  <div className="text-sm text-muted-foreground">
                    <div>ID: {selectedReview.booking.id}</div>
                    <div>Check-in: {formatDate(selectedReview.booking.check_in)}</div>
                    <div>Check-out: {formatDate(selectedReview.booking.check_out)}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 